import type { ReactNode } from 'react';
import type { LiveStatistics } from '@/domain/live-statistics';
import { formatTicketStatPair } from '@/domain/my-bets-live';
import { cn } from '@/lib/cn';

export interface TicketBetStatsWidgetProps {
  statistics: LiveStatistics | null;
  homeScore?: string;
  awayScore?: string;
  className?: string;
}

/**
 * Inline My Bets football stats strip (legacy `statswidget`):
 * goals, yellow, red, corners, penalties — home above away.
 */
export function TicketBetStatsWidget({
  statistics,
  homeScore = '0',
  awayScore = '0',
  className,
}: TicketBetStatsWidgetProps) {
  const yellow = formatTicketStatPair(statistics?.yellowCards);
  const red = formatTicketStatPair(statistics?.redCards);
  const corners = formatTicketStatPair(statistics?.corners);
  const penalties = formatTicketStatPair(statistics?.penalties);

  return (
    <div
      className={cn(
        'flex w-full min-w-0 max-w-full items-stretch justify-between gap-0.5 overflow-hidden rounded-md bg-bg-elevated px-1.5 py-1.5 sm:gap-1 sm:px-2',
        className,
      )}
      data-section="statswidget"
    >
      <StatColumn
        home={homeScore}
        away={awayScore}
        icon={<BallIcon />}
        label="Score"
      />
      <StatColumn
        home={yellow.home}
        away={yellow.away}
        icon={<CardGlyph tone="warning" />}
        label="Yellow cards"
      />
      <StatColumn
        home={red.home}
        away={red.away}
        icon={<CardGlyph tone="danger" />}
        label="Red cards"
      />
      <StatColumn
        home={corners.home}
        away={corners.away}
        icon={<CornerGlyph />}
        label="Corners"
      />
      <StatColumn
        home={penalties.home}
        away={penalties.away}
        icon={<PenaltyGlyph />}
        label="Penalties"
      />
    </div>
  );
}

function StatColumn({
  home,
  away,
  icon,
  label,
}: {
  home: string;
  away: string;
  icon: ReactNode;
  label: string;
}) {
  return (
    <div className="flex min-w-0 flex-1 basis-0 flex-col items-center gap-0.5 overflow-hidden text-center">
      <span className="max-w-full truncate text-[11px] font-bold tabular-nums text-text-primary">
        {home}
      </span>
      <span className="max-w-full truncate text-[11px] font-bold tabular-nums text-text-primary">
        {away}
      </span>
      <span
        className="flex h-3.5 w-full items-center justify-center text-icon-muted"
        aria-label={label}
      >
        {icon}
      </span>
    </div>
  );
}

function BallIcon() {
  return (
    <svg viewBox="0 0 12 12" className="size-3" aria-hidden>
      <circle cx="6" cy="6" r="4.5" fill="none" stroke="currentColor" strokeWidth="1" />
      <path
        d="M6 1.5v9M1.5 6h9M3.2 3.2l5.6 5.6M8.8 3.2l-5.6 5.6"
        fill="none"
        stroke="currentColor"
        strokeWidth="0.7"
      />
    </svg>
  );
}

function CardGlyph({ tone }: { tone: 'danger' | 'warning' }) {
  return (
    <span
      className={cn(
        'inline-block h-3 w-2 rotate-12 rounded-[1.5px]',
        tone === 'danger' ? 'bg-status-danger' : 'bg-status-warning',
      )}
      aria-hidden
    />
  );
}

function CornerGlyph() {
  return (
    <span className="relative size-3" aria-hidden>
      <span className="absolute top-0 left-0 h-2 w-0.5 bg-text-primary" />
      <span className="absolute bottom-0 left-0 h-0.5 w-2 bg-text-primary" />
    </span>
  );
}

function PenaltyGlyph() {
  return (
    <svg viewBox="0 0 12 12" className="size-3 text-text-primary" aria-hidden>
      <circle cx="6" cy="6" r="4.5" fill="none" stroke="currentColor" strokeWidth="1" />
      <path d="M4 6h4" stroke="currentColor" strokeWidth="1" />
    </svg>
  );
}
